import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Layout from '@components/Layout';
import {
  Button,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  RadioGroup,
  Alert,
  AlertDescription
} from '@see-ui/core';
import { ChevronDownIcon, UserRoundIcon } from 'lucide-react';
import moment from 'moment';
import * as service from '@services/api/member-pharmacy-service';
import { useGlobalStore } from '@services/store/global-store';
import UpdateDeliveryAddressSlider from './UpdateDeliveryAddressSlider';
import UpdateSettings from './UpdateCommunicationSettingsSlider';
import UpdateAllergySlider from './UpdateAllergySlider';
import ScheduleRefill from './ScheduleRefill';
import { set } from 'react-hook-form';
import PaymentMethodsCard from './PaymentMethodsCard';

const CartPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cartPrescription = location.state?.cart || [];
  // const patientData = location.state?.patientData;
  const { memberDetails } = useGlobalStore((state) => state);
  const [successfullySubmitted, setSuccessfullySubmitted] =
    useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<boolean>(false);
  const [deliveryAddress, setDeliveryAddress] = React.useState<any>(null);
  const [allDeliveryAddress, setAllDeliveryAddress] = React.useState<any>(null);
  const [showDeliveryAddressSlider, setShowDeliveryAddressSlider] =
    useState<boolean>(false);
  const [showAllergySliderToUpdate, setShowAllergySliderToUpdate] =
    useState<boolean>(false);
  const [showCommSettingsSlider, setShowCommSettingsSlider] =
    useState<boolean>(false);

  const [shippingOptions, setShippingOptions] = useState<any[]>([]);
  const [commPreferences, setCommPreferences] = React.useState<any[]>([]);
  const [selectedShippingOption, setSelectedShippingOption] =
    useState<any>(null);
  const [selectedShippingCode, setSelectedShippingCode] = useState<string>('');
  const [allergies, setAllergies] = useState<any[]>([]);
  const [notificationMap, setNotificationMap] = React.useState<any>();
  const [subtotal, setSubtotal] = useState<number>(0);
  const [outstandingBalance, setOutstandingBalance] = useState<number>(0);
  const [shippingCost, setShippingCost] = useState<number>(0);
  const [estimatedTotal, setEstimatedTotal] = useState<number>(0);
  const [paymentMethods, setPaymentMethods] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [pendingDrugNames, setPendingDrugNames] = useState<string[]>([]);

  useEffect(() => {
    setShippingCost(
      selectedShippingOption?.shippingCost
        ? Number(selectedShippingOption.shippingCost)
        : 0
    );
  }, [selectedShippingOption]);

  useEffect(() => {
    setEstimatedTotal(subtotal + outstandingBalance + shippingCost);
  }, [subtotal, outstandingBalance, shippingCost]);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (estimatedTotal > 0) {
        const getPaymentMethodResp = await service.getPaymentMethod({
          hcid: memberDetails?.member?.memberPolicy?.hcid
        });
        if (getPaymentMethodResp?.data) {
          setPaymentMethods(getPaymentMethodResp.data?.paymentMethods);
          const defaultMethod = getPaymentMethodResp.data?.paymentMethods?.find(
            (pm: any) => pm.isDefaultPaymentMethod
          );
          setSelectedPaymentMethod(defaultMethod);
        }
      } else {
        setPaymentMethods(null);
      }
    };
    fetchPaymentHistory();
  }, [estimatedTotal]);

  const handleUpdatePaymentMethods = (newMethods: any[]) => {
    setPaymentMethods(newMethods);
  };

  const fetchPharmacyAddresses = async () => {
    try {
      const req = {
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      const response = await service.pharmacyAddresses(req);
      if (response?.data && response.data.addresses?.length > 0) {
        setAllDeliveryAddress(response.data.addresses);
        const primaryAddress = response.data.addresses.find(
          (address: any) => address.isPrimary === true
        );
        setDeliveryAddress(primaryAddress);
      }
    } catch (error) {
      console.error('Error fetching pharmacy addresses:', error);
    }
  };

  const fetchPharmacyShippingOptions = async () => {
    try {
      const req = {
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      const response = await service.getPharmacyShippingOptions(req);
      setShippingOptions(response?.data?.deliveryTimelines || []);
    } catch (error) {
      console.error('Error fetching pharmacy shipping options:', error);
    }
  };

  const fetchCommunicationPreferences = async () => {
    try {
      const req = {
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      const response = await service.getCommunicationPreferences(req);
      setCommPreferences(response.data.commPreferences);
      const notificationMap = processCommunicationPreferences(
        response.data.commPreferences
      );
      setNotificationMap(notificationMap);
    } catch (error) {
      console.error(
        'Error fetching pharmacy communication preferences:',
        error
      );
    }
  };

  const processCommunicationPreferences = (commPreferences: any[]) => {
    const notificationMap: Record<string, string[]> = {
      OS: [],
      RR: []
    };

    commPreferences.forEach((preference) => {
      if (preference.communicationChannel) {
        if (preference.notifications.includes('OS')) {
          notificationMap.OS.push(preference.communicationChannelTypeCode);
        }
        if (preference.notifications.includes('RR')) {
          notificationMap.RR.push(preference.communicationChannelTypeCode);
        }
      }
    });
    // Object.entries(notificationMap).forEach(([key, value]) => {
    //   console.log(`Notification Type: ${key}, Channels: ${value.join(', ')}`);
    // });

    return notificationMap;
  };

  const fetchOutStandingBalance = async () => {
    try {
      const req = {
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      const response = await service.getPharmacyOutstandingBalance(req);
      if (response?.data[0]?.patientBalance) {
        setOutstandingBalance(Number(response.data[0].patientBalance));
      }
    } catch (error) {
      console.error('Error fetching outstanding balance:', error);
    }
  };

  const fetchPharmacyAllergies = async () => {
    try {
      const req = {
        hcid: memberDetails?.member?.memberPolicy?.hcid
      };
      const response = await service.getPharmacyAllergies(req);
      setAllergies(response?.data?.members);
    } catch (error) {
      console.error('Error fetching pharmacy allergies:', error);
    }
  };
  const submitOrder = async () => {
    const refills: any[] = [];
    const renewals: any[] = [];
    cartPrescription.forEach((item) => {
      const base = {
        addressId: deliveryAddress?.id,
        patientResponse: true,
        rxNumber: item.rxNumber,
        shippingType: selectedShippingOption?.shippingTypeCode || 'STNDRD',
        srcOrdrId: item.srcOrdrId,
        facilityId: item?.childPrescriptions[0]?.facilityID
        //pamAmount: estimatedTotal?.toFixed(2)
      };
      if (item.refillStatus === 'Ready for Refill') {
        refills.push(base);
      } else {
        renewals.push(base);
      }
    });

    const request = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      pbmRxOrder: {
        estimatedCost: estimatedTotal?.toFixed(2),
        paymentDate: moment().format('YYYY-MM-DD'),
        refills,
        renewals,
        secondaryInsurance: false,
        tokenId: selectedPaymentMethod?.tokenId
      }
    };
    // console.log('Submit Order Request:', request);
    const response: any = await service.submitRefillRenew(request);
    if (response?.status === 'SUCCESS') {
      const pendingPbmDetails = response?.data?.pbmDetails?.pendingRxs;
      let pendingDrugNames: string[] = [];
      if (pendingPbmDetails && pendingPbmDetails.length > 0) {
        pendingPbmDetails.forEach((item) => {
          const matchingPrescription = cartPrescription.find(
            (pending: any) => pending.rxNumber === item.rxNumber
          );
          if (matchingPrescription) {
            pendingDrugNames.push(matchingPrescription?.drugLabel);
          }
        });

        //TODO
        setPendingDrugNames(pendingDrugNames);
        setErrorStatus(true);
      } else {
        setSuccessfullySubmitted(true);
      }
    } else {
      setErrorStatus(true);
    }
  };
  useEffect(() => {
    fetchPharmacyShippingOptions();
    fetchPharmacyAddresses();
    fetchCommunicationPreferences();
    fetchPharmacyAllergies();
    fetchOutStandingBalance();
  }, []);
  //  console.log(cartPrescription);

  return (
    <Layout title="Cart">
      {!successfullySubmitted ? (
        <div className="app-container p-40">
          <div className="flex justify-end">
            <Button variant="link" onClick={() => navigate(-1)}>
              Back to Prescriptions
            </Button>
          </div>
          <div className="border-l-4 border-red-500 pl-4">
            <h1 className="text-2xl font-semibold mb-9">
              Review Prescriptions
            </h1>
          </div>
          <div className="bg-blue-100 p-4 rounded-md mb-4">
            <p className="font-semibold flex items-center">
              <UserRoundIcon className="mr-2" />
              {cartPrescription[0]?.memberName} (DOB{' '}
              {cartPrescription[0]?.dob
                ? moment(cartPrescription[0].dob).isValid()
                  ? moment(cartPrescription[0].dob).format('MM/DD/YYYY')
                  : 'Invalid Date'
                : 'N/A'}
              )
            </p>
          </div>
          {cartPrescription.length > 0 ? (
            <>
              <Accordion type="multiple">
                {cartPrescription.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger
                      className="justify-start flex text-lg hover:no-underline items-start"
                      showDefaultArrow={false}
                    >
                      <ChevronDownIcon className="h-8 w-8 text-blue-500 " />
                      <strong>
                        {item?.drugLabel || 'Unknown Drug'} - Rx Number:{' '}
                        {item?.rxNumber}
                      </strong>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="p-4 bg-gray-50 rounded-md">
                        <div className="grid grid-cols-6 md:grid-cols-6 gap-4 pt-8">
                          <div>
                            <label className="font-semibold">
                              Fulfilled By
                            </label>
                            <div>
                              <span>{item.fulfilledBy}</span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Days Supply Quantity
                            </label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.daysSupplyQuantity}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Last Filled Date
                            </label>
                            <div>
                              <span>{item.lastFillDate}</span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Prescribed By
                            </label>
                            <div>
                              <span>{item.prescriberName} </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Refills Left
                            </label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.refillsLeft}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Last Price Paid
                            </label>
                            <div>
                              <span>
                                ${item.childPrescriptions[0]?.lastPaid}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-6 md:grid-cols-6 gap-4 pt-8">
                          <div>
                            <label className="font-semibold">
                              Estimated Cost
                            </label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.estimatedCost}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Prescriber Phone
                            </label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.prescriberPhone}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">Issue Date</label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.rxIssueDate}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">
                              Expiration Date
                            </label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.expirationDate}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="font-semibold">Directions</label>
                            <div>
                              <span>
                                {item.childPrescriptions[0]?.dosageDirections}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              {!cartPrescription[0]?.scheduleRefill && (
                <div className="grid grid-cols-1 gap-4 pt-8 mt-8 border p-4 rounded-md">
                  <div className="flex justify-between border-b p-2">
                    <p>subtotal</p>
                    <p>${subtotal?.toFixed(2)}</p>
                  </div>
                  <div className="flex justify-between border-b p-2">
                    <p>Outstanding Balance</p>
                    <p>${outstandingBalance?.toFixed(2)}</p>
                  </div>
                  <div className="flex justify-between border-b p-2">
                    <p>Shipping</p>
                    <p>${shippingCost?.toFixed(2)}</p>
                  </div>
                  <div className="flex justify-between p-2 font-semibold">
                    <p>Estimated Total*</p>
                    <p>${estimatedTotal?.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p>Your cart is empty.</p>
          )}

          <div className="mt-5">
            <h3 className="mb-3 font-light">Delivery Address</h3>
            <div className="flex items-center w-full">
              <span className="font-semibold mr-4">
                {deliveryAddress?.addressLine1}, {deliveryAddress?.cityName},{' '}
                {deliveryAddress?.state} {deliveryAddress?.zipCode}
                {'-'}
                {deliveryAddress?.zipCodeSuffix}
              </span>
              <div className="ml-auto">
                <Button
                  rounded
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDeliveryAddressSlider(true)}
                >
                  Edit
                </Button>
                {showDeliveryAddressSlider && (
                  <UpdateDeliveryAddressSlider
                    onClose={() => setShowDeliveryAddressSlider(false)}
                    flowType="cart"
                    onSelectAddress={(address) => {
                      setDeliveryAddress(address);
                      setShowDeliveryAddressSlider(false);
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <hr className="border-gray-300 my-5" />
          {cartPrescription &&
          cartPrescription.length &&
          cartPrescription[0]?.scheduleRefill ? (
            <ScheduleRefill />
          ) : (
            <>
              <div className="mt-5">
                <h3 className="mb-3 font-light">Delivery Date</h3>
                <span className="font-semibold">
                  Estimated Delivery Timeline
                </span>
                <div className="flex flex-wrap gap-9 mt-5">
                  {shippingOptions.map((option) => (
                    <div
                      key={option.shippingTypeCode}
                      className="border p-6 rounded-md w-full md:w-auto"
                    >
                      <RadioGroup
                        data-testid={`shipping-option-${option.shippingTypeCode}`}
                        value={selectedShippingCode}
                        options={[
                          {
                            label: `${
                              option.shippingTypeCode === 'STNDRD'
                                ? 'Standard Shipping'
                                : option.shippingTypeCode === 'EXPDT'
                                  ? 'Expedited Shipping'
                                  : option.shippingTypeCode
                            } - Cost: $${option.shippingCost.toFixed(2)} | Delivery: ${moment(
                              option.deliveryTimelineStart
                            ).format('MM/DD/YYYY')} - ${moment(
                              option.deliveryTimelineEnd
                            ).format('MM/DD/YYYY')}`,
                            value: option.shippingTypeCode
                          }
                        ]}
                        onValueChange={(code) => {
                          setSelectedShippingCode(code);
                          const selected = shippingOptions.find(
                            (opt) => opt.shippingTypeCode === code
                          );
                          setSelectedShippingOption(selected);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <hr className="border-gray-300 my-5" />

              {paymentMethods && (
                <>
                  <div className="mt-5">
                    <PaymentMethodsCard
                      paymentMethods={paymentMethods}
                      onUpdatePaymentMethods={handleUpdatePaymentMethods}
                      onSelectPaymentMethod={(method) =>
                        setSelectedPaymentMethod(method)
                      }
                    />{' '}
                  </div>
                  <hr className="border-gray-300 my-5" />
                </>
              )}

              <div className="mt-5">
                <div className="flex items-center w-full">
                  <h3 className="mb-3 font-light">Communication Settings</h3>
                  <div className="ml-auto">
                    <Button
                      rounded
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowCommSettingsSlider(true)}
                    >
                      Change
                    </Button>
                    {showCommSettingsSlider && (
                      <UpdateSettings
                        onClose={() => setShowCommSettingsSlider(false)}
                        commPreferences={commPreferences}
                        notificationMap={notificationMap}
                        onPreferencesUpdated={fetchCommunicationPreferences}
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center w-full">
                  <div>
                    {['OS', 'RR'].map((notificationType) => (
                      <div key={notificationType} className="mb-4">
                        <h4 className="font-semibold">
                          {notificationType === 'OS'
                            ? 'Order Status'
                            : notificationType === 'RR'
                              ? 'Refill Notices'
                              : notificationType}
                        </h4>
                        {commPreferences
                          .filter((pref) =>
                            pref.notifications.includes(notificationType)
                          )
                          .map((pref, index, array) => (
                            <p
                              key={index}
                              className="text-sm inline-block mr-2"
                            >
                              {pref.communicationChannelTypeCode === 'Push'
                                ? pref.communicationChannelTypeDescription
                                : pref.communicationChannel}
                              {index < array.length - 1 ? ',' : ''}
                            </p>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <hr className="border-gray-300 my-5" />

              <div className="mt-5">
                <div className="flex items-center w-full">
                  <h3 className="mb-3 font-light">Allergies</h3>
                  <div className="ml-auto">
                    <Button
                      rounded
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowAllergySliderToUpdate(true)}
                    >
                      Change
                    </Button>
                    {showAllergySliderToUpdate && (
                      <UpdateAllergySlider
                        allergies={allergies}
                        onSubmit={() => {
                          fetchPharmacyAllergies();
                          setShowAllergySliderToUpdate(false);
                        }}
                        onClose={() => setShowAllergySliderToUpdate(false)}
                      />
                    )}
                  </div>
                </div>
                {allergies[0]?.allergies.length > 0 ? (
                  <div>
                    <p className="font-semibold">
                      {`${allergies[0].firstName} ${allergies[0].lastName}`}
                    </p>
                    <ul className="list-disc pl-5 mt-2">
                      {allergies[0]?.allergies.map((allergy, index) => (
                        <li key={index}>{allergy.name}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p>No allergies found.</p>
                )}
              </div>
              {pendingDrugNames.length > 0 && (
                <Alert className="mt-4 mb-4" variant="error">
                  <AlertDescription>
                    The following prescriptions are failed:
                    <ul className="list-disc list-inside mt-2 text-left font-normal">
                      {pendingDrugNames.map((name, idx) => (
                        <li key={idx}>{name}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              {errorStatus && (
                <Alert className="mt-4" variant="error">
                  <AlertDescription>
                    Sorry, looks like something isn't working. Please give us
                    some time to fix it. Then, try again. If the problem
                    continues, please contact your administrator for assistance.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end">
                <Button className="mt-8" onClick={submitOrder}>
                  Submit Order
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="app-container flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-full max-w-md mt-4 mb-8 p-6 border-2 border-green-500 bg-green-50 rounded text-center font-semibold text-green-700">
              Your order has been successfully submitted.
            </div>
            <Button variant="link" onClick={() => navigate(-1)}>
              Back to Prescriptions
            </Button>
          </div>
        </>
      )}
    </Layout>
  );
};

export default CartPage;
